// A person reads and signs their own engagement schedule (My events). The server
// accepts the signature only from the person the contract is for, signed in, and
// only against the exact version shown here (its SHA-256 is sent back with it).
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack,
  Typography,
} from "@mui/material";
import PdfPreview from "./PdfPreview";
import SignaturePad from "./SignaturePad";
import { downloadContractFile, fetchContractLink } from "./ContractSummary";
import { CONTRACTS_API, fmtDateTime, signatureFor } from "../contracts";
import { useContractConfig } from "../useContractConfig";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import { notify } from "../notify";

/** Props: contract (ContractRead, or null when closed), onClose, onSigned(updated),
 *  blockers — what the record lacks before signing (core/personnel_readiness.py);
 *  the server refuses a signature while there are any. */
export default function MyContractDialog({ contract, onClose, onSigned, blockers = [] }) {
  const navigate = useNavigate();
  const config = useContractConfig();
  const fullScreen = useFullScreenDialog();
  const [shown, setShown] = useState(contract);
  const { busy, run } = useWriteLock();

  useEffect(() => { if (contract) setShown(contract); }, [contract]);

  const doc = shown?.documents?.[0];
  const ss = doc ? signatureFor(doc, "shelter_stone") : null;
  const mine = doc ? signatureFor(doc, "counterparty") : null;
  const done = shown?.status === "completed";

  const sign = (signature) => run(() => axios.post(`${CONTRACTS_API}/${shown.id}/countersign`, {
    ...signature, document_id: doc.id, sha256: doc.current_sha256,
  }).then(res => {
    setShown(res.data);
    onSigned?.(res.data);
    notify(res.data.status === "completed"
      ? "Signed — a copy has been emailed to you." : "Signed.", "success");
  }));

  return (
    <Dialog open={!!contract} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        {doc?.title || "Engagement schedule"}
        <Typography variant="body2" color="text.secondary">{shown?.event_label}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {doc && (
          <Stack spacing={2}>
            {done ? (
              <Alert severity="success">
                Signed by you on {fmtDateTime(mine?.signed_at)} and by Shelter Stone. A copy was emailed to
                {" "}{shown.signer_email}; you can download it here at any time.
              </Alert>
            ) : shown.status === "signed" ? (
              <Alert severity="info">
                You've signed. The final copy is being prepared and will be emailed to you.
              </Alert>
            ) : ss && (
              <Typography variant="body2" color="text.secondary">
                Signed for Shelter Stone by {ss.signer_name} on {fmtDateTime(ss.signed_at)}. Please read it,
                then sign below.
              </Typography>
            )}

            <PdfPreview title={doc.title} height={480}
                        reloadKey={`${shown.id}-${done ? "signed" : doc.current_sha256}`}
                        load={() => fetchContractLink(shown, doc, done ? "signed" : "current")} />

            {shown.status === "sent" && blockers.length > 0 && (
              <Alert severity="warning"
                     action={<Button color="inherit" size="small"
                                     onClick={() => { onClose(); navigate("/my-details?edit=1"); }}>My details</Button>}>
                Before you can sign, please add these to My details: {blockers.join("; ")}.
              </Alert>
            )}
            {shown.status === "sent" && blockers.length === 0 && (
              <>
                <Divider />
                <SignaturePad defaultName={shown.signer_name || ""} busy={busy}
                              consentText={config?.consent?.personnel || "I agree to sign electronically."}
                              onSign={sign} />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done && doc && (
          <Box sx={{ mr: "auto" }}>
            <Button onClick={() => downloadContractFile(shown, doc, "signed")}>Download signed copy</Button>
          </Box>
        )}
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
